import { useState, useEffect } from "react";
import { Shield, Plus, Trash2, Search, Loader2, Ban, Clock } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";

const AdminIpBansTab = () => {
  const { toast } = useToast();
  const [bans, setBans] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    ip_address: "",
    reason: "Violação dos termos de uso",
    expires_days: "",
  });

  const fetchBans = async () => {
    setLoading(true);
    const { data } = await supabase
      .from("banned_ips")
      .select("*")
      .order("created_at", { ascending: false });
    setBans(data || []);
    setLoading(false);
  };

  useEffect(() => { fetchBans(); }, []);

  const handleAdd = async () => {
    if (!form.ip_address.trim()) {
      toast({ title: "Informe o endereço IP", variant: "destructive" });
      return;
    }
    setSaving(true);

    const { data: { user } } = await supabase.auth.getUser();

    const insertData: any = {
      ip_address: form.ip_address.trim(),
      reason: form.reason || "Violação dos termos de uso",
      banned_by: user?.id,
      is_active: true,
    };

    if (form.expires_days && parseInt(form.expires_days) > 0) {
      const expires = new Date();
      expires.setDate(expires.getDate() + parseInt(form.expires_days));
      insertData.expires_at = expires.toISOString();
    }

    const { error } = await supabase.from("banned_ips").insert(insertData);
    if (error) {
      if (error.message.includes("duplicate")) {
        toast({ title: "Este IP já está banido", variant: "destructive" });
      } else {
        toast({ title: "Erro ao banir IP", description: error.message, variant: "destructive" });
      }
    } else {
      toast({ title: "IP banido com sucesso" });
      setShowAdd(false);
      setForm({ ip_address: "", reason: "Violação dos termos de uso", expires_days: "" });
      fetchBans();
    }
    setSaving(false);
  };

  const toggleBan = async (id: string, currentActive: boolean) => {
    await supabase.from("banned_ips").update({ is_active: !currentActive }).eq("id", id);
    fetchBans();
    toast({ title: currentActive ? "Ban desativado" : "Ban reativado" });
  };

  const deleteBan = async (id: string) => {
    await supabase.from("banned_ips").delete().eq("id", id);
    fetchBans();
    toast({ title: "Ban removido" });
  };

  const filtered = bans.filter(b =>
    b.ip_address.includes(search) || (b.reason || "").toLowerCase().includes(search.toLowerCase())
  );

  const isExpired = (ban: any) => ban.expires_at && new Date(ban.expires_at) < new Date();

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Shield className="h-5 w-5 text-destructive" />
          <h2 className="text-lg font-bold font-display">IPs Banidos</h2>
          <Badge variant="secondary">{bans.length}</Badge>
        </div>
        <Button onClick={() => setShowAdd(true)} size="sm">
          <Plus className="h-4 w-4 mr-1" /> Banir IP
        </Button>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Buscar por IP ou motivo..."
          className="pl-9 h-10"
        />
      </div>

      {loading ? (
        <div className="flex justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          <Ban className="h-10 w-10 mx-auto mb-2 opacity-30" />
          <p>Nenhum IP banido</p>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map(ban => (
            <div
              key={ban.id}
              className={`flex items-center justify-between p-3 rounded-xl border transition-all ${
                ban.is_active && !isExpired(ban)
                  ? "border-destructive/20 bg-destructive/5"
                  : "border-border bg-card opacity-60"
              }`}
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <code className="text-sm font-mono font-bold">{ban.ip_address}</code>
                  {ban.is_active && !isExpired(ban) ? (
                    <Badge variant="destructive" className="text-[10px]">Ativo</Badge>
                  ) : isExpired(ban) ? (
                    <Badge variant="secondary" className="text-[10px]">Expirado</Badge>
                  ) : (
                    <Badge variant="secondary" className="text-[10px]">Inativo</Badge>
                  )}
                </div>
                <p className="text-xs text-muted-foreground mt-0.5">{ban.reason}</p>
                <div className="flex gap-3 text-[10px] text-muted-foreground mt-1">
                  <span>Criado: {new Date(ban.created_at).toLocaleDateString("pt-BR")}</span>
                  {ban.expires_at && (
                    <span className="flex items-center gap-0.5">
                      <Clock className="h-3 w-3" />
                      Expira: {new Date(ban.expires_at).toLocaleDateString("pt-BR")}
                    </span>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2 ml-2">
                <Switch
                  checked={ban.is_active}
                  onCheckedChange={() => toggleBan(ban.id, ban.is_active)}
                />
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-destructive hover:text-destructive"
                  onClick={() => deleteBan(ban.id)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Banir Endereço IP</DialogTitle>
            <DialogDescription>
              O IP será bloqueado de fazer cadastros e pedidos na plataforma.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label className="text-sm">Endereço IP *</Label>
              <Input
                value={form.ip_address}
                onChange={e => setForm(f => ({ ...f, ip_address: e.target.value }))}
                placeholder="Ex: 192.168.1.1"
                className="font-mono"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm">Motivo</Label>
              <Textarea
                value={form.reason}
                onChange={e => setForm(f => ({ ...f, reason: e.target.value }))}
                rows={2}
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm">Expiração (dias)</Label>
              <Input
                type="number"
                value={form.expires_days}
                onChange={e => setForm(f => ({ ...f, expires_days: e.target.value }))}
                placeholder="Deixe vazio para permanente"
              />
            </div>
            <Button onClick={handleAdd} disabled={saving} className="w-full">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Ban className="h-4 w-4 mr-2" />}
              Banir IP
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminIpBansTab;
